/**
 * Calorie Burn Calculator
 *
 * Uses established exercise science formulas:
 * - Running: based on MET values adjusted for pace and body weight
 * - Weight training: based on MET values, duration, intensity (sets × reps)
 * - All formulas use body weight in lbs (the app's standard unit)
 *
 * Reference: Compendium of Physical Activities (Ainsworth et al.)
 */

// ─── Running Calorie Burn ───

/**
 * Calculate calories burned from a run.
 *
 * Formula: Calories = MET × weight(kg) × duration(hours)
 * MET is derived from pace — faster pace = higher MET.
 *
 * @param {number} distanceMi - distance in miles
 * @param {number} durationSec - duration in seconds
 * @param {number} weightLbs - body weight in pounds
 * @param {number} elevationFt - elevation gain in feet (optional)
 * @returns {number} estimated calories burned
 */
export function calcRunCalories(distanceMi, durationSec, weightLbs, elevationFt = 0) {
  if (!distanceMi || !durationSec || !weightLbs) return 0;

  const weightKg = weightLbs * 0.453592;
  const durationHrs = durationSec / 3600;
  const paceMinPerMi = (durationSec / 60) / distanceMi;

  // MET values from Compendium of Physical Activities
  // Mapped to pace ranges (min/mile)
  let met;
  if (paceMinPerMi <= 5.5) met = 18.0;       // <5:30/mi — elite
  else if (paceMinPerMi <= 6.0) met = 16.0;   // 5:30-6:00
  else if (paceMinPerMi <= 6.5) met = 14.5;   // 6:00-6:30
  else if (paceMinPerMi <= 7.0) met = 13.5;   // 6:30-7:00
  else if (paceMinPerMi <= 7.5) met = 12.5;   // 7:00-7:30
  else if (paceMinPerMi <= 8.0) met = 11.5;   // 7:30-8:00
  else if (paceMinPerMi <= 8.5) met = 10.5;   // 8:00-8:30
  else if (paceMinPerMi <= 9.0) met = 9.8;    // 8:30-9:00
  else if (paceMinPerMi <= 9.5) met = 9.0;    // 9:00-9:30
  else if (paceMinPerMi <= 10.0) met = 8.3;   // 9:30-10:00
  else if (paceMinPerMi <= 11.0) met = 7.5;   // 10:00-11:00
  else if (paceMinPerMi <= 12.0) met = 6.5;   // 11:00-12:00
  else met = 5.5;                               // >12:00 (walk/jog)

  // Elevation adjustment: +3% per 100ft of gain
  if (elevationFt > 0) {
    met *= 1 + (elevationFt / 100) * 0.03;
  }

  const calories = met * weightKg * durationHrs;
  return Math.round(calories);
}

// ─── Weight Training Calorie Burn ───

/**
 * Calculate calories burned from a weight training session.
 *
 * Uses MET values for resistance training, adjusted by:
 * - Duration of the session
 * - Volume (total completed sets) as a proxy for intensity
 * - Body weight
 *
 * Base MET for weight training: 3.5 (light) to 6.0 (vigorous)
 *
 * @param {number} durationMin - workout duration in minutes
 * @param {number} completedSets - number of completed sets
 * @param {number} weightLbs - body weight in pounds
 * @param {number} rpe - rate of perceived exertion (6-10, optional)
 * @returns {number} estimated calories burned
 */
export function calcWorkoutCalories(durationMin, completedSets, weightLbs, rpe = null) {
  if (!durationMin || !weightLbs) return 0;

  const weightKg = weightLbs * 0.453592;
  const durationHrs = durationMin / 60;

  // Base MET for resistance training
  // Light (bodyweight/easy) = 3.5, Moderate = 5.0, Vigorous = 6.0
  let met = 5.0;

  // Adjust MET based on volume density (sets per minute)
  if (completedSets > 0) {
    const setsPerMin = completedSets / durationMin;
    if (setsPerMin >= 0.6) met = 6.0;       // High density (e.g., 30 sets in 50 min)
    else if (setsPerMin >= 0.4) met = 5.5;   // Moderate density
    else if (setsPerMin >= 0.2) met = 5.0;   // Normal density
    else met = 4.0;                            // Low density (lots of rest)
  }

  // RPE adjustment: if user reported effort
  if (rpe !== null) {
    if (rpe >= 9) met *= 1.15;
    else if (rpe >= 8) met *= 1.05;
    else if (rpe <= 6) met *= 0.85;
  }

  const calories = met * weightKg * durationHrs;
  return Math.round(calories);
}

// ─── Aggregate Daily Burn ───

/**
 * Calculate total calories burned from exercise today.
 *
 * @param {Array} workouts - today's completed workouts
 * @param {Array} runs - today's logged runs
 * @param {number} weightLbs - body weight in pounds
 * @param {Array} exercises - exercise definitions (for category info)
 * @returns {{ total, workoutBurn, runBurn, details }}
 */
export function calcTodayBurn(workouts, runs, weightLbs) {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(todayStart);
  todayEnd.setDate(todayEnd.getDate() + 1);

  let workoutBurn = 0;
  const workoutDetails = [];

  for (const w of (workouts || [])) {
    if (w.startTime < todayStart.getTime() || w.startTime >= todayEnd.getTime()) continue;
    const duration = w.endTime ? Math.round((w.endTime - w.startTime) / 60000) : 0;
    if (duration <= 0) continue;

    let completedSets = 0;
    for (const block of w.exerciseBlocks || []) {
      for (const set of block.sets || []) {
        if (set.done) completedSets++;
      }
    }

    const cal = calcWorkoutCalories(duration, completedSets, weightLbs, w.rpe);
    workoutBurn += cal;
    workoutDetails.push({ name: w.name, calories: cal, duration });
  }

  let runBurn = 0;
  const runDetails = [];

  for (const r of (runs || [])) {
    if (r.timestamp < todayStart.getTime() || r.timestamp >= todayEnd.getTime()) continue;
    const distMi = r.unit === "km" ? r.distance * 0.621371 : r.distance;
    const cal = calcRunCalories(distMi, r.duration, weightLbs, r.elevation);
    runBurn += cal;
    runDetails.push({ type: r.runType, distance: distMi, calories: cal });
  }

  return {
    total: workoutBurn + runBurn,
    workoutBurn,
    runBurn,
    workoutDetails,
    runDetails,
  };
}

// ─── Adaptive Nutrition Adjustment ───

/**
 * Adjust macro targets based on actual calories burned today.
 *
 * Philosophy:
 * - The TDEE calculation already assumes "average" training days
 * - On days you burn MORE than average, eat more (especially carbs)
 * - On rest days, eat at or slightly below base targets
 * - Protein stays constant (recovery needs don't drop on rest days)
 *
 * @param {object} baseMacros - base macro goals { calories, protein, carbs, fat, fiber }
 * @param {number} exerciseBurn - total calories burned from exercise today
 * @param {boolean} isTrainingDay - did the user train today?
 * @param {string} goal - user's goal (lose_fat, build_muscle, etc.)
 * @returns {object} adjusted macro goals
 */
export function adjustMacrosForBurn(baseMacros, exerciseBurn, isTrainingDay, goal) {
  if (!baseMacros) return baseMacros;

  // Average expected daily burn is already baked into TDEE
  // We estimate the "assumed" daily burn based on base calories
  // TDEE typically assumes ~300-500 cal of exercise on training days
  const assumedBurn = 400;

  // How much more (or less) than assumed?
  const burnDelta = isTrainingDay ? exerciseBurn - assumedBurn : -assumedBurn * 0.3;

  // Don't adjust if delta is small (±100 cal)
  if (Math.abs(burnDelta) < 100) return baseMacros;

  // How much of the burn delta to eat back depends on goal:
  // - Cutting: eat back 40% (preserve energy but maintain deficit)
  // - Maintaining: eat back 65%
  // - Gaining: eat back 80% (fuel recovery and growth)
  const eatBackRate =
    goal === "lose_fat" ? 0.40 :
    goal === "build_muscle" || goal === "get_stronger" ? 0.80 :
    0.65;

  const calAdjust = Math.round(burnDelta * eatBackRate);

  // Distribute extra calories: 60% carbs, 25% fat, 15% protein
  const extraCarbs = Math.round((calAdjust * 0.60) / 4);
  const extraFat = Math.round((calAdjust * 0.25) / 9);
  const extraProtein = Math.round((calAdjust * 0.15) / 4);

  return {
    calories: Math.max(baseMacros.calories + calAdjust, baseMacros.calories * 0.85),
    protein: Math.max(baseMacros.protein + extraProtein, baseMacros.protein), // Never reduce protein
    carbs: Math.max(0, baseMacros.carbs + extraCarbs),
    fat: Math.max(Math.round(baseMacros.fat * 0.8), baseMacros.fat + extraFat),
    fiber: baseMacros.fiber,
  };
}
